import { useEffect, useMemo, useState } from "react";
import {
  App,
  Button,
  Card,
  Col,
  Form,
  Input,
  Row,
  Space,
  Switch,
  Tabs,
  Tooltip,
  Typography,
} from "antd";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { LegalDocument } from "@glow-price/shared";

import { legalDocsApi, type LegalDocKey } from "../api/resources";

type FormValues = Pick<
  LegalDocument,
  "version" | "title" | "bodyMarkdown" | "requireReconsent"
>;

/**
 * 法务文档编辑器。运营/法务可在此对隐私政策与用户协议进行直接编辑；
 * 一旦保存：
 *   1. 全站 GET /api/legal/<key> 60s edge cache 失效后即生效
 *   2. 若 requireReconsent=true，前端 H5 / miniprogram 会在用户下次进入时
 *      弹层强制重新签署
 *   3. 写入 audit_logs 一行 (entity_type='legal_doc')
 */
export function LegalDocsPage() {
  const [activeKey, setActiveKey] = useState<LegalDocKey>("privacy");

  return (
    <div>
      <Typography.Title level={4} style={{ marginTop: 0 }}>
        法务文档管理
      </Typography.Title>
      <Typography.Paragraph type="secondary" style={{ marginTop: -8 }}>
        编辑隐私政策与用户协议。大改时请**升 version** 并打开"升版后强制重签"开关。
        保存后约 60 秒边缘缓存失效即对所有终端生效。
      </Typography.Paragraph>

      <Tabs
        activeKey={activeKey}
        onChange={(k) => setActiveKey(k as LegalDocKey)}
        items={[
          { key: "privacy", label: "隐私政策", children: <LegalEditor docKey="privacy" /> },
          { key: "terms", label: "用户协议", children: <LegalEditor docKey="terms" /> },
        ]}
      />
    </div>
  );
}

function LegalEditor({ docKey }: { docKey: LegalDocKey }) {
  const qc = useQueryClient();
  const { message } = App.useApp();
  const [form] = Form.useForm<FormValues>();

  const query = useQuery({
    queryKey: ["legal-doc", docKey],
    queryFn: () => legalDocsApi.get(docKey),
  });

  useEffect(() => {
    if (query.data) {
      form.setFieldsValue({
        version: query.data.version,
        title: query.data.title,
        bodyMarkdown: query.data.bodyMarkdown,
        requireReconsent: query.data.requireReconsent,
      });
    }
  }, [query.data, form]);

  const saveMutation = useMutation({
    mutationFn: (values: FormValues) => legalDocsApi.put(docKey, values),
    onSuccess: () => {
      message.success("已保存，~60 秒后边缘缓存失效即生效");
      qc.invalidateQueries({ queryKey: ["legal-doc", docKey] });
    },
    onError: (err: Error) => message.error(err.message),
  });

  // Watch only bodyMarkdown for the preview so we don't re-render the whole
  // form tree on every keystroke in the other fields.
  const liveBody = Form.useWatch("bodyMarkdown", form);
  const previewBody = useMemo(
    () => liveBody ?? query.data?.bodyMarkdown ?? "",
    [liveBody, query.data?.bodyMarkdown],
  );

  return (
    <Row gutter={[16, 16]}>
      <Col xs={24} lg={14}>
        <Card title={docKey === "privacy" ? "隐私政策" : "用户协议"} loading={query.isLoading}>
          <Form<FormValues>
            form={form}
            layout="vertical"
            onFinish={(values) => saveMutation.mutate(values)}
          >
            <Form.Item
              name="version"
              label="版本号"
              rules={[{ required: true, max: 64 }]}
              tooltip={'重要修订时升号，例如 "privacy-2026-06-v1" → "privacy-2026-07-v1"'}
            >
              <Input placeholder="privacy-2026-06-v1" />
            </Form.Item>

            <Form.Item
              name="title"
              label="文档标题"
              rules={[{ required: true, max: 120 }]}
            >
              <Input placeholder="Glow Price 隐私政策" />
            </Form.Item>

            <Form.Item
              name="bodyMarkdown"
              label="正文（Markdown）"
              rules={[{ required: true, min: 20, max: 50000 }]}
              tooltip="支持标题 (# ##)、列表 (- 1.)、加粗 (**) 与表格"
            >
              <Input.TextArea
                rows={20}
                style={{ fontFamily: "ui-monospace, Menlo, Consolas, monospace", fontSize: 12 }}
              />
            </Form.Item>

            <Form.Item
              name="requireReconsent"
              label={
                <Space>
                  升版后强制重新签署
                  <Tooltip title="开启后所有 consent_privacy_version / consent_terms_version 不等于当前 version 的用户在下次进入时会被强制弹层重新同意">
                    <span style={{ color: "#999" }}>?</span>
                  </Tooltip>
                </Space>
              }
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>

            <Form.Item>
              <Button type="primary" htmlType="submit" loading={saveMutation.isPending}>
                保存
              </Button>
            </Form.Item>
          </Form>
        </Card>
      </Col>

      <Col xs={24} lg={10}>
        <Card title="预览（原始 markdown）">
          <Typography.Paragraph type="secondary" style={{ marginTop: 0 }}>
            前端 H5 / 小程序按 markdown 渲染。下方为编辑器中的原始文本。
          </Typography.Paragraph>
          <pre
            style={{
              maxHeight: 520,
              overflow: "auto",
              background: "#0d0d0d",
              color: "#e6e6e6",
              padding: 12,
              borderRadius: 4,
              fontSize: 12,
              lineHeight: 1.6,
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          >
            {previewBody || "（暂无内容）"}
          </pre>
        </Card>
      </Col>
    </Row>
  );
}
